import type { Path } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { FloatingLabelNumberInput } from '@ahm/react-ui';
import {
  ActionIcon,
  Box,
  Card,
  Group,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';

import { useDebouncedValue } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DetectMaxCodSource,
  useDetectCODState,
  useDetectMaxCod,
  type UserWithEKYC,
} from '../../hooks/use-detect-cod-state';
import CodAlert from './cod-alert';
import LockIcon from '@/assets/icons/lock-icon.svg';
import XCircle from '@/assets/icons/xcircle-icon.svg';
import InfoTooltip from '@/components/info-tooltip';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type PathCodProps = {
  handleOpenModal: (modalId: any) => void;
  handleInputChange: (field: keyof Path, value: number | string) => void;
  currentPath?: Path;
};

export default function PathCod({
  handleOpenModal,
  handleInputChange,
  currentPath,
}: PathCodProps) {
  const {
    control,
    formState: { errors },
    setError,
    setValue,
    trigger,
    clearErrors,
  } = useFormContext();
  const tBooking = useTranslations('booking');
  const { selectedService } = useBookingStore((state) => state);
  const isMobile = useIsMobile();
  const { session } = useAuth();
  const user = session?.user;
  const { disabled: ekycDisabled } = useDetectCODState(user as UserWithEKYC);

  const itemValue = useWatch({ control, name: 'item_value' });
  const [debouncedItemValue] = useDebouncedValue(itemValue, 300);

  const codValue = useWatch({ control, name: 'cod' });
  const [debouncedCod] = useDebouncedValue(codValue, 300);

  const detectMaxCodResult = useDetectMaxCod(
    user!,
    selectedService!,
    debouncedCod
  );
  const maxCodValue = detectMaxCodResult.value;
  const detectMaxCodSource = detectMaxCodResult?.source;

  useEffect(() => {
    let errorMessage: string | undefined;
    if (maxCodValue) {
      if (detectMaxCodSource === DetectMaxCodSource.USER) {
        errorMessage = tBooking(
          'location.cod_limits_error_input_for_user_max_cod',
          {
            max_cod: formatCurrency(maxCodValue),
          }
        );
      }

      if (detectMaxCodSource === DetectMaxCodSource.SERVICE) {
        errorMessage = tBooking(
          'location.cod_limits_error_input_for_service_max_cod',
          {
            max_cod: formatCurrency(maxCodValue),
          }
        );
      }
    }

    if (errorMessage) {
      setError('cod', {
        type: 'max',
        message: errorMessage,
      });
    } else {
      clearErrors('cod');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectMaxCodResult, detectMaxCodSource]);

  const handleCodChange = async (value: number | string) => {
    handleInputChange('cod', value);

    if (typeof value === 'number') {
      if (value < 0) {
        setError('cod', {
          type: 'min',
          message: tBooking('location.min_cod_error'),
        });
        return;
      }

      // setValue('cod', value);

      await trigger('cod');
    }
  };

  const handleBlurCOD = () => {
    if (debouncedItemValue || currentPath?.item_value) return;

    // Auto fill item_value by COD value
    if (codValue && !debouncedItemValue) {
      handleInputChange('item_value', codValue);
    }
  };

  return (
    <Card radius={0} py={rem(12)} px="md">
      <Card.Section inheritPadding py="sm">
        <Group gap={rem(4)} align="center">
          {isMobile ? (
            <Title order={4}>{tBooking('location.cod')}</Title>
          ) : (
            <Text fz={16} fw={700} tt="uppercase" lts={0.5} c="neutral.7">
              {tBooking('location.cod')}
            </Text>
          )}
          <Box mt={rem(-4)}>
            <InfoTooltip
              label={tBooking('location.modal_learn_about_cod_title')}
              onClick={() => handleOpenModal('max-cod')}
            />
          </Box>
        </Group>
      </Card.Section>

      <Stack gap={rem(12)} p="md" pt={0} component={Card.Section}>
        <Controller
          name="cod"
          control={control}
          render={({ field }) => (
            <FloatingLabelNumberInput
              {...field}
              min={0}
              label={tBooking('location.enter_amount')}
              disabled={ekycDisabled || selectedService?.max_cod === undefined}
              // rightSection={ekycDisabled ? <LockIcon /> : <></>}

              rightSection={
                <>
                  {field.value ? (
                    <ActionIcon
                      aria-label="Clear item_value"
                      variant="transparent"
                      onClick={() => {
                        void handleCodChange(0);
                      }}
                    >
                      <XCircle />
                    </ActionIcon>
                  ) : ekycDisabled ? (
                    <LockIcon />
                  ) : (
                    <></>
                  )}
                </>
              }
              onChange={(value) => {
                void handleCodChange(value);
              }}
              onBlur={handleBlurCOD}
              error={errors.cod?.message as string}
              styles={{
                error: {
                  fontWeight: 500,
                },
              }}
            />
          )}
        />

        <CodAlert />
      </Stack>
    </Card>
  );
}
